// A reusable page section: optional bilingual eyebrow + heading, on a chosen
// surface, with a one-shot scroll-reveal (fadeUp) wrapping its children.
// Owned by Agent D (page-only component).
//
// Renders the brand's signature header device itself (Latin letter-spaced caption
// above a bold heading) with Tailwind + brand tokens, so the section header is
// styled consistently in both locales without depending on Agent C's CSS classes.
import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { fadeUp, stagger, revealViewport } from "../motion/variants";

type Surface = "white" | "tint" | "tint-3";

const surfaceClass: Record<Surface, string> = {
  white: "bg-white",
  tint: "bg-[var(--brand-tint-3)]",
  "tint-3": "bg-[var(--brand-tint-3)]",
};

export interface SectionBlockProps {
  /** Wide letter-spaced ALL-CAPS Latin eyebrow (the brand's signature device). */
  eyebrowEn?: string;
  /**
   * Large section heading — the caller MUST pass the LOCALE-RESOLVED text
   * (zh bundle = Chinese, en bundle = English) so /en renders an English heading.
   * (Previously a fixed-Chinese `eyebrowZh` was used as the heading, which left
   * English pages showing Chinese headings — see polish-fix §3.)
   */
  heading?: string;
  /** Optional lead paragraph under the heading. */
  lead?: string;
  /** Center the header block. */
  centered?: boolean;
  surface?: Surface;
  id?: string;
  className?: string;
  children?: ReactNode;
}

export default function SectionBlock({
  eyebrowEn,
  heading,
  lead,
  centered = false,
  surface = "white",
  id,
  className = "",
  children,
}: SectionBlockProps) {
  // Heading text is the locale-resolved string the caller passed (no Chinese fallback).
  const headingText = heading;
  const hasHeader = eyebrowEn || headingText || lead;
  // zh-first: in Chinese mode the heading leads and the Latin caption follows below.
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith("zh") ?? true;

  const captionEl = eyebrowEn ? (
    <p
      className={`font-sans text-[var(--text-sm)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-[var(--brand-azure-text)]${
        isZh ? " mt-3" : ""
      }`}
    >
      {eyebrowEn}
    </p>
  ) : null;
  const headEl = headingText ? (
    <h2
      className={`font-zh text-[var(--text-3xl)] font-bold leading-[var(--leading-heading)] text-[var(--ink)]${
        isZh ? "" : " mt-4"
      }`}
    >
      {headingText}
    </h2>
  ) : null;

  return (
    <section
      id={id}
      className={`${surfaceClass[surface]} py-[var(--space-section)] ${className}`}
    >
      <div className="mx-auto w-full max-w-6xl px-6">
        {hasHeader && (
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={revealViewport}
            className={`mb-12 max-w-3xl ${centered ? "mx-auto text-center" : ""}`}
          >
            {isZh ? (
              <>
                {headEl}
                {captionEl}
              </>
            ) : (
              <>
                {captionEl}
                {headEl}
              </>
            )}
            {lead && (
              <p className="mt-5 text-[var(--text-lg)] leading-[var(--leading-body)] text-[var(--brand-slate-text)]">
                {lead}
              </p>
            )}
          </motion.div>
        )}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          {children}
        </motion.div>
      </div>
    </section>
  );
}
